import { FormattedMessage } from 'react-intl';
import { MarkdownConverter } from '@synergy/components-next';
import { PaloNoteTone, PaloNotice, PaloNotices, paloNoticeText } from '@synergy/components-palo';
import { ReactElement, useEffect, useState } from 'react';

import { InternalNote } from '../../models/internal-note';
import { InternalNoteFormValue, InternalNotesModal } from '../internal-notes/internal-notes-modal';
import { InternalNoteType } from '../../models/enums/internal-note-type';
import { NoteType } from '../../models/enums/note-type';
import { fetchInternalNotes, updateInternalNote } from '../../actions/internal-notes-actions';
import { internalNotesSelector } from '../../selectors/internal-notes-selectors';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

/** The colour a notice is filed under, as a tone of the notice block. */
export const noteTone = (type: InternalNoteType): PaloNoteTone => {
    switch (type) {
        case InternalNoteType.Danger:
            return 'danger';
        case InternalNoteType.Warn:
            return 'warn';
        default:
            return 'info';
    }
};

/** Red before orange before blue — the order the notices are maintained in. */
export const NOTE_WEIGHT: Record<PaloNoteTone, number> = { danger: 0, warn: 1, info: 2 };

export interface PaloRecordNoticesProps {
    noteType: NoteType;
    /** Omit while the record has no id yet; nothing is shown then. */
    entityId?: number;
}

/**
 * The notices of a master record, written out in the head of its mask.
 *
 * On a document they already stand there, and they are the same notices — a
 * supplier's notice is shown on every order it appears on. On the supplier
 * itself they sat in a section of the overview tab, so whoever opened the
 * conditions or the categories never saw them. Here they stand in the part of
 * the mask that every tab shares.
 *
 * Adding and removing stays in the overview section; from here a notice can be
 * corrected, which is what the document masks allow as well.
 */
export const PaloRecordNotices = ({ noteType, entityId }: PaloRecordNoticesProps): ReactElement | null => {
    const notes = useAppSelector(internalNotesSelector);
    const dispatch = useThunkDispatch();

    const [noteToEdit, setNoteToEdit] = useState<InternalNote | undefined>(undefined);

    useEffect(() => {
        if (entityId) {
            dispatch(fetchInternalNotes(noteType, entityId));
        }
    }, [noteType, entityId, dispatch]);

    const handleSave = async (internalNote: InternalNoteFormValue): Promise<void> => {
        if (internalNote.id) {
            await dispatch(updateInternalNote({ ...internalNote, id: internalNote.id }));
        }
        setNoteToEdit(undefined);
    };

    const sorted = [...(notes ?? [])].sort((a, b) => NOTE_WEIGHT[noteTone(a.type)] - NOTE_WEIGHT[noteTone(b.type)]);

    if (!entityId || 0 === sorted.length) {
        return null;
    }

    return (
        <>
            <PaloNotices
                groups={[
                    sorted.map(note => {
                        const written = paloNoticeText(note.text);

                        return (
                            <PaloNotice
                                key={note.id}
                                tone={noteTone(note.type)}
                                title={written.title}
                                editLabel={<FormattedMessage id="app.common.edit" />}
                                onEdit={() => setNoteToEdit(note)}
                            >
                                <MarkdownConverter value={written.body} />
                            </PaloNotice>
                        );
                    }),
                ]}
            />

            <InternalNotesModal
                internalNote={noteToEdit}
                open={undefined !== noteToEdit}
                onClose={() => setNoteToEdit(undefined)}
                onSave={handleSave}
            />
        </>
    );
};
